import { useCallback } from 'react';
import groupBy from 'lodash/groupBy';
import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/types.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { ROOT_LEVEL } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { BaseIssueNode } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import { useUpdateIssueParent } from '@atlassian/jira-business-list-handlers/src/controllers/update-issue-parent/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	JWMListViewItemDetails_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMListViewItemDetailsNode,
	JWMListViewItemDetails,
} from '../../../../services/get-list-view-issues/__generated_apollo__/JWMListViewItemDetails';
import { LIST_VIEW_QUERY } from '../../../../services/get-list-view-issues/gql.tsx';
import type { ListViewIssuesReturn } from '../../../../services/get-list-view-issues/types.tsx';
import {
	addChildToParentListView,
	removeChildFromParentListView,
} from '../../../../services/get-list-view-issues/utils.tsx';
import { useIssueFields } from '../../../get-issue-fields/index.tsx';
import { useHierarchyStore } from '../../../hierarchy/hierarchy-store/index.tsx';
import { transformData } from './utils/index.tsx';

export const useBulkEditCacheActions = () => {
	const { updateItemsInCache } = useCacheActions();
	const [{ issuesMap, issueParentMap, childrenItems }] = useHierarchyStore();

	const { getQueryFieldIds } = useIssueFields();

	const queryFieldIds = getQueryFieldIds();

	const { key: projectKey } = useProject();

	const { updateIssueParent, unlinkParentIssue } = useUpdateIssueParent<
		ListViewIssuesReturn,
		JWMListViewItemDetailsNode,
		JWMListViewItemDetails
	>({
		childrenQueryData: childrenItems,
		fieldIds: queryFieldIds,
		issuesMap,
		issueParentMap,
		cacheQuery: LIST_VIEW_QUERY,
		removeChildFromParent: removeChildFromParentListView<JWMListViewItemDetailsNode>,
		addChildToParent: addChildToParentListView<JWMListViewItemDetailsNode>,
		view: 'list',
		packageName: 'jiraBusinessList',
		shouldOnlyUpdateCache: true,
		projectKey,
	});

	const updateSelectedItemsInCache = useCallback(
		({ data, issueIds }: IssuesUpdatePayload) => {
			const selectedIssuesByParent = groupBy(
				[...issueIds.map((issueId) => issueId.toString())],
				(issueId) => issuesMap.get(issueId)?.parentIssueId || ROOT_LEVEL,
			);

			const parents = Object.entries(selectedIssuesByParent);

			const optimisticUpdateIssue = (existingIssue: BaseIssueNode) => {
				const updatedIssue = {
					...existingIssue,
					fields: [
						...(existingIssue.fields?.filter(
							(field) => !Object.keys(data).includes(field?.fieldId),
						) ?? []),
						...transformData(existingIssue, data),
					],
				};

				return updatedIssue;
			};

			parents.forEach(([, issues]) => {
				const variables = issuesMap.get(issues[0])?.cacheVariables;

				if (variables !== undefined) {
					updateItemsInCache({
						query: LIST_VIEW_QUERY,
						variables,
						issueIds: issues,
						updateItem: optimisticUpdateIssue,
					});

					if (data?.[PARENT_TYPE] !== undefined) {
						issues.forEach((issueId) => {
							if (data?.[PARENT_TYPE]?.id) {
								const parentIssue = issuesMap.get(data?.[PARENT_TYPE].id.toString());
								if (parentIssue) {
									updateIssueParent({
										issueKey: issueId,
										parentIssue,
									});
								}
							} else {
								unlinkParentIssue({
									issueKey: issueId,
								});
							}
						});
					}
				}
			});
		},
		[issuesMap, updateItemsInCache, updateIssueParent, unlinkParentIssue],
	);

	return { updateSelectedItemsInCache };
};
